import {type Brand} from './utils/brands';
import {findMatchingIdentityForLoginHint} from './utils/login-hint';

const api =
    process.env.NODE_ENV === 'production'
        ? require('@baikal/api-client')
        : require('./baikal-api-client-stub');

export type AuthenticationContext = {
    identifier: string;
    type: 'phone_number' | 'email' | 'uid';
    activated_roles: any[];
};

export type IdentityResponse = {
    acr: string;
    last_active_auth: string;
    sso_token: string;
    partial: boolean;
    name?: string;
    authentication_context: Array<AuthenticationContext>;
};

export type UserAccount = {
    identifier: string;
    type: 'phone_number' | 'email' | 'uid';
    name?: string;
    ssoToken: string;
    lastActiveTime: number;
    partial: boolean;
};

export const maskName = (name: string | undefined): string | undefined => {
    if (!name) {
        return name;
    }
    return name
        .split(' ')
        .map((part) => {
            if (!part.length) {
                return part;
            }
            const visibleChars = 3;
            return part.substring(0, visibleChars) + '***';
        })
        .join(' ');
};

const maskPhoneNumber = (phoneNumber: string): string => {
    return phoneNumber.replace(/(.{3}).*(\d{4})/, '$1***$2');
};

const maskEmail = (email: string): string => {
    const [user, domain] = email.split('@');
    const userParts = user.split('.');
    const domainParts = domain.split('.');

    const maskPart = (part: string): string => {
        const visibleChars = part.length < 3 ? 1 : 2;
        const numAsterisks = part.length <= 3 ? 1 : 3;
        return part.substring(0, visibleChars) + '*'.repeat(numAsterisks);
    };

    return userParts.map(maskPart).join('.') + '@' + domainParts.map(maskPart).join('.');
};

const maskNif = (nif: string): string => {
    return nif.replace(/.*(.{4})/, '***$1');
};

export const maskIdentifier = (context: Pick<AuthenticationContext, 'identifier' | 'type'>): string => {
    switch (context.type) {
        case 'phone_number':
            return maskPhoneNumber(context.identifier);
        case 'email':
            return maskEmail(context.identifier);
        case 'uid':
            return maskNif(context.identifier);
        default:
            return context.identifier;
    }
};

export const createUserAccount = (identity: IdentityResponse): UserAccount => ({
    identifier: maskIdentifier(identity.authentication_context[0]),
    type: identity.authentication_context[0].type,
    name: maskName(identity.name),
    ssoToken: identity.sso_token,
    lastActiveTime: new Date(identity.last_active_auth).getTime(),
    partial: !!identity.partial,
});

export const login = (ssoToken: string): Promise<string> =>
    new Promise((resolve, reject) => {
        api.login(ssoToken, (error: any, result: string) => {
            if (error) {
                reject(error);
            } else {
                resolve(result);
            }
        });
    });

export const redirectToLogin = (): Promise<string> =>
    new Promise((resolve, reject) => {
        api.login(null, (error: any, result: string) => {
            if (error) {
                reject(error);
            } else {
                resolve(result);
            }
        });
    });

export const getAccounts = (
    brand: Brand,
    clientId: string,
    acrValues: Array<string>,
    loginHint?: string
): Promise<Array<UserAccount>> =>
    new Promise((resolve, reject) => {
        api.getAccountsInfo(clientId, acrValues, (error: any, result: IdentityResponse[]) => {
            if (error) {
                reject(error);
            } else {
                if (loginHint) {
                    const matchingIdentity = findMatchingIdentityForLoginHint(result, loginHint, brand);
                    if (matchingIdentity) {
                        resolve([createUserAccount(matchingIdentity)]);
                        return;
                    } else {
                        // In case there is no matching identity for the provided loginHint, we redirect to login
                        resolve([]);
                        return;
                    }
                }
                resolve(
                    result
                        .map((identity) => createUserAccount(identity))
                        .sort((a, b) => b.lastActiveTime - a.lastActiveTime)
                );
            }
        });
    });

export const logout = (ssoToken: string): Promise<void> =>
    new Promise((resolve, reject) => {
        api.logout(ssoToken, (error: any, result: any) => {
            if (error) {
                reject(error);
            } else {
                resolve(result);
            }
        });
    });

export const remember = (value: boolean): Promise<string> =>
    new Promise((resolve, reject) => {
        api.remember(value, (error: any, result: string) => {
            if (error) {
                reject(error);
            } else {
                resolve(result);
            }
        });
    });

type AuthParams = {
    acrValues: Array<string>;
    clientId?: string;
    clientName?: string;
    loginHint?: string;
};

let memoizedAuthParams: AuthParams = {acrValues: []};
let prevLocationSearch = '';

/**
 * Returns URL auth params. This is not an API call, simply parses current URL
 *
 * Result is memoized so values can be used as hook dependencies
 */
export const getAuthParams = (): AuthParams => {
    if (prevLocationSearch === window.location.search) {
        return memoizedAuthParams;
    }
    const params = api.getAuthParams();
    const result = {
        acrValues: params.acr_values || [],
        clientId: params.client_id,
        clientName: params.client_name,
        loginHint: params.login_hint,
    };
    memoizedAuthParams = result;
    prevLocationSearch = window.location.search;
    return result;
};
